import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client'
import { IncomingMessage, ServerResponse } from 'http'
import type { ParsedUrlQuery } from 'querystring'
import { getTheatricalRelease } from 'src/services/Theatrical'
import { slugs } from '@/constants/slugs'
import { BASIC_PAGE_SLUGS } from '@/constants/theatricalUtils'
import { ServerExperiments } from '@/experimentation'
import { ExperimentParamsArray, runExperimentsOnServer } from '@/experimentation/server'
import { TicketsLayout } from '@/layouts/TicketsLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getGuildStatus } from '@/services/GuildUserService/GuildUserService'
import { getProjectGuildScore } from '@/services/ProjectsService'
import { getTheatricalReleasePromos } from '@/services/Theatrical/contentfulQueries'
import {
  getGiveawaySchedule,
  getUserGuildDiscounts,
  getValidPublicDiscountsForRelease,
  GiveawaySchedule,
  TheatricalReleaseResult,
  ValidPublicDiscounts,
} from '@/services/Theatrical/queries'
import { getUserAgent } from '@/services/UserAgentService'
import { TheatricalReleaseRegion } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'
import { ShowtimesView } from '@/views/TicketCheckoutViews/ShowtimesView'
import { InitializedReservation, initReservation } from '@/views/TicketCheckoutViews/ShowtimesView/initReservation'
import { TheatricalPromotion } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/types'
import { pickCurrentPromo } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/utils'

const UPSERT_THEATRICAL_USER_PREFERENCES = gql`
  mutation UpsertTheatricalUserPreferences($input: UserPreferenceInput!) {
    upsertTheatricalUserPreferences(input: $input) {
      id
      preferredVenues {
        id
        name
        providerId
      }
    }
  }
`

interface TicketsPageProps {
  theatricalRelease: TheatricalReleaseResult
  theatricalSlug: string
  showOverlayVideoExperiment?: boolean
  isDecrementingDiscount?: boolean
  projectGuildScore: number | null
  experiments?: ServerExperiments
  reservation: InitializedReservation
  locale: string
  publicDiscounts: ValidPublicDiscounts
  giveawaySchedule?: GiveawaySchedule | null
  promo: TheatricalPromotion | null
  basicPageExperimentEnabled?: boolean
}

export const TicketsHypebotPage: NextPageWithLayout<TicketsPageProps> = ({
  theatricalRelease,
  theatricalSlug,
  projectGuildScore,
  publicDiscounts,
  experiments,
  reservation,
  giveawaySchedule,
  promo,
  basicPageExperimentEnabled = false,
}) => {
  return (
    <TicketsLayout
      shouldShowLanguageModal
      isDarkMode={basicPageExperimentEnabled ? false : true}
      useDefaultNav
      regions={theatricalRelease?.theatricalReleaseRegions as TheatricalReleaseRegion[]}
    >
      <ShowtimesView
        reservation={reservation}
        theatricalRelease={theatricalRelease}
        theatricalSlug={theatricalSlug}
        projectGuildScore={projectGuildScore}
        experiments={experiments}
        publicDiscounts={publicDiscounts}
        giveawaySchedule={giveawaySchedule}
        promo={promo}
        basicPageExperimentEnabled={basicPageExperimentEnabled}
      />
    </TicketsLayout>
  )
}
export const getServerSideProps = getServerSidePropsErrorHandler<TicketsPageProps>(
  async ({ federationClient, locale, next404, params, res, req, query, userId, anonymousId }) => {
    const theatricalSlug = params?.slug
    if (!theatricalSlug || Array.isArray(theatricalSlug)) {
      return next404()
    }

    const hasPromo = getItemOrFirstEntry(query?.promo) != null

    // If we have a preferred_venue_id, process it first
    if (query.preferred_venue_id && userId) {
      await setPreferredVenueFromQueryParams(query, userId, federationClient)

      // Prepare clean URL for redirect
      const { preferred_venue_id, ...cleanedQuery } = query
      const queryString = new URLSearchParams(cleanedQuery as Record<string, string>).toString()

      return {
        redirect: {
          destination: `/${locale}/tickets/${theatricalSlug}${queryString ? `?${queryString}` : ''}`,
          permanent: false,
        },
      }
    }

    // Skip reservation creation for bots to prevent unnecessary reservations
    const userAgentHeader = req.headers['user-agent']
    const { isBot } = getUserAgent(userAgentHeader)

    const [
      reservation,
      theatricalRelease,
      projectGuildScore,
      publicDiscounts,
      guildStatus,
      guildCodes,
      giveawaySchedule,
      promos,
      translations,
    ] = await Promise.all([
      // Only create reservation for real users, not bots/crawlers
      isBot ? Promise.resolve(null) : initReservation({ query, federationClient, theatricalSlug, anonymousId }),
      getTheatricalRelease({ theatricalSlug, client: federationClient }),
      getProjectGuildScore(theatricalSlug, federationClient),
      getValidPublicDiscountsForRelease(federationClient, theatricalSlug),
      getGuildStatus(federationClient),
      getUserGuildDiscounts(federationClient, userId, theatricalSlug),
      getGiveawaySchedule(federationClient, theatricalSlug),
      getTheatricalReleasePromos({ theatricalSlug }),
      loadTranslations(locale, ['common', 'home', 'tickets', 'chatbot', 'account', 'theatrical-presales']),
    ])

    if (!theatricalRelease) {
      return next404()
    }

    const isPremiumGuildUser = !!guildStatus?.isGuildMember && !!guildStatus?.guildRoles?.hasFreeTickets

    const promo = await pickCurrentPromo({
      promos: promos ?? null,
      query,
      theatricalSlug,
      hasGuildTickets: isPremiumGuildUser ? !!guildCodes?.length : false,
      publicDiscounts,
      giveawaySchedule,
      req,
      res,
    })

    const soloMioBasicExperiment = await runExperimentsOnServer(req, res, [
      {
        experimentKey: 'solo-basic-showtime-page',
        defaultValue: false,
        skip: theatricalSlug !== slugs.soloMio || hasPromo,
      },
    ])

    const basicPageExperimentEnabled = Boolean(
      BASIC_PAGE_SLUGS.has(theatricalSlug) || soloMioBasicExperiment['solo-basic-showtime-page'],
    )

    const experiments = await runTicketExperiments({
      theatricalSlug,
      req,
      res,
      query,
      userId,
      promo,
      basicPageExperimentEnabled,
    })

    return {
      props: {
        ...translations,
        theatricalSlug,
        theatricalRelease,
        reservation,
        locale,
        experiments: experiments ?? null,
        publicDiscounts,
        guildCodes,
        giveawaySchedule,
        promo,
        projectGuildScore: projectGuildScore?.guildScore,
        basicPageExperimentEnabled,
      },
    }
  },
)

TicketsHypebotPage.getLayout = (page) => {
  return page
}

export default TicketsHypebotPage

async function runTicketExperiments({
  req,
  res,
  userId,
  promo,
  basicPageExperimentEnabled,
  theatricalSlug,
}: {
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
  theatricalSlug: string
  query: ParsedUrlQuery
  userId: string | null
  promo: TheatricalPromotion | null
  basicPageExperimentEnabled: boolean
}): Promise<ServerExperiments> {
  const expConfigs: ExperimentParamsArray = []

  expConfigs.push({
    experimentKey: 'preferred-theater-vanilla-layout',
    defaultValue: false,
    skip: !basicPageExperimentEnabled || !userId,
  })

  expConfigs.push({
    experimentKey: 'basic-page-no-showtimes-v2',
    defaultValue: false,
    skip: !basicPageExperimentEnabled,
  })

  expConfigs.push({
    experimentKey: 'david-showtimes-trailer',
    defaultValue: false,
    skip: theatricalSlug !== slugs.david,
  })

  expConfigs.push({
    experimentKey: 'david-nofees-promo-3',
    defaultValue: false,
    skip: theatricalSlug !== slugs.david || promo?.promoType !== 'no_fees',
  })

  expConfigs.push({
    experimentKey: 'david-sing-along-filter',
    defaultValue: false,
    skip: theatricalSlug !== slugs.david || promo?.id !== 'SINGALONG',
  })

  try {
    const experiments = await runExperimentsOnServer(req, res, expConfigs)
    return experiments || {}
  } catch (error) {
    logger().error('Failed to run experiments:', error)
    return expConfigs.reduce((acc, { experimentKey, defaultValue }) => {
      acc[experimentKey] = defaultValue
      return acc
    }, {} as ServerExperiments)
  }
}

const setPreferredVenueFromQueryParams = async (
  query: ParsedUrlQuery,
  userId: string | null,
  federationClient: ApolloClient<NormalizedCacheObject>,
) => {
  if (!userId) return

  if (query?.preferred_venue_id) {
    const input = {
      theaterPreferences: [
        {
          id: query.preferred_venue_id as string,
          priority: 0,
        },
      ],
    }

    try {
      await federationClient.mutate({
        mutation: UPSERT_THEATRICAL_USER_PREFERENCES,
        variables: { input },
      })
    } catch (err) {
      logger().error('Error setting preferred venue:', err)
    }
  }
}
